import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Crosshair, X, Search, AlertCircle } from 'lucide-react';

// Haversine distance in km
const calcDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

// Open Google Maps directions
const openDirections = (fromLat, fromLng, toLat, toLng, toLabel) => {
  const url = `https://www.google.com/maps/dir/?api=1&origin=${fromLat},${fromLng}&destination=${toLat},${toLng}&travelmode=driving`;
  window.open(url, '_blank');
};

// Open shop on Google Maps
const openShopOnMap = (lat, lng, label) => {
  const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  window.open(url, '_blank');
};

export { calcDistance, openDirections, openShopOnMap };

// ─── Location Picker Modal ───────────────────────────────────────────────────
export function LocationPickerModal({ onClose, onSelect, title = 'Set Your Location' }) {
  const [tab, setTab] = useState('gps'); // 'gps' | 'manual'
  const [gpsStatus, setGpsStatus] = useState('idle'); // idle | loading | success | error
  const [gpsData, setGpsData] = useState(null);
  const [gpsError, setGpsError] = useState('');
  const [manual, setManual] = useState({ street: '', city: '', state: '', pincode: '', lat: '', lng: '' });
  const [searchQuery, setSearchQuery] = useState('');

  const detectGPS = () => {
    if (!navigator.geolocation) {
      setGpsError('GPS not supported by your browser');
      setGpsStatus('error');
      return;
    }
    setGpsStatus('loading');
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        // Reverse geocode using Nominatim (free, no key needed)
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
          const data = await res.json();
          const addr = data.address || {};
          setGpsData({
            lat, lng, accuracy,
            street: addr.road || addr.neighbourhood || '',
            city: addr.city || addr.town || addr.village || addr.county || '',
            state: addr.state || '',
            pincode: addr.postcode || '',
            display: data.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`
          });
          setGpsStatus('success');
        } catch {
          setGpsData({ lat, lng, accuracy, street: '', city: '', state: '', pincode: '', display: `${lat.toFixed(4)}, ${lng.toFixed(4)}` });
          setGpsStatus('success');
        }
      },
      (err) => {
        setGpsStatus('error');
        if (err.code === 1) setGpsError('Location permission denied. Please allow location access.');
        else if (err.code === 2) setGpsError('Location unavailable. Try manual entry.');
        else setGpsError('Location timeout. Try manual entry.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleGpsSelect = () => {
    if (!gpsData) return;
    onSelect({
      lat: gpsData.lat, lng: gpsData.lng,
      street: gpsData.street, city: gpsData.city,
      state: gpsData.state, pincode: gpsData.pincode,
      display: gpsData.display
    });
  };

  const handleManualSelect = async () => {
    const { street, city, state, pincode } = manual;
    if (!city || !state) { alert('Please enter at least City and State'); return; }
    let lat = parseFloat(manual.lat) || 0;
    let lng = parseFloat(manual.lng) || 0;
    // Try to geocode if no coords
    if (!lat || !lng) {
      try {
        const q = encodeURIComponent(`${street} ${city} ${state} ${pincode} India`);
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`);
        const data = await res.json();
        if (data[0]) { lat = parseFloat(data[0].lat); lng = parseFloat(data[0].lon); }
      } catch {}
    }
    onSelect({ lat, lng, street, city, state, pincode, display: `${street ? street + ', ' : ''}${city}, ${state}` });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 540, maxHeight: '90vh', overflow: 'auto', padding: '0 0 24px' }} className="fade-in">
        {/* Header */}
        <div style={{ padding: '20px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontWeight: 700, fontSize: 17, display: 'flex', alignItems: 'center', gap: 8 }}>
            <MapPin size={18} color="var(--orange)" /> {title}
          </h3>
          <button onClick={onClose} style={{ background: 'var(--gray-light)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--gray-light)', borderRadius: 10, padding: 4, margin: '0 20px 20px' }}>
          {[['gps', '📍 Use GPS'], ['manual', '✏️ Enter Manually']].map(([t, l]) => (
            <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: '9px', borderRadius: 8, border: 'none', background: tab === t ? 'white' : 'transparent', color: tab === t ? 'var(--orange)' : 'var(--gray)', fontWeight: 600, fontSize: 13, cursor: 'pointer', boxShadow: tab === t ? 'var(--shadow)' : 'none' }}>{l}</button>
          ))}
        </div>

        <div style={{ padding: '0 20px' }}>
          {/* GPS Tab */}
          {tab === 'gps' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {gpsStatus === 'idle' && (
                <button onClick={detectGPS} className="btn-primary" style={{ justifyContent: 'center', padding: '14px', fontSize: 15 }}>
                  <Crosshair size={18} /> Detect My Location
                </button>
              )}
              {gpsStatus === 'loading' && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div className="spinner" style={{ margin: '0 auto 14px' }} />
                  <div style={{ color: 'var(--gray)', fontSize: 14 }}>Detecting your location...</div>
                  <div style={{ color: 'var(--gray)', fontSize: 12, marginTop: 4 }}>Please allow location access if prompted</div>
                </div>
              )}
              {gpsStatus === 'error' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 14px', display: 'flex', gap: 10, color: 'var(--red)', fontSize: 13 }}>
                    <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} /> {gpsError}
                  </div>
                  <button onClick={detectGPS} className="btn-outline" style={{ justifyContent: 'center' }}>Try Again</button>
                  <button onClick={() => setTab('manual')} style={{ background: 'none', border: 'none', color: 'var(--orange)', fontWeight: 600, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>Enter address manually instead</button>
                </div>
              )}
              {gpsStatus === 'success' && gpsData && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 12, padding: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <MapPin size={18} color="var(--green)" style={{ flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Location Detected</div>
                        <div style={{ color: 'var(--gray)', fontSize: 13, lineHeight: 1.5 }}>{gpsData.display}</div>
                        <div style={{ color: 'var(--gray)', fontSize: 11, marginTop: 6 }}>
                          📍 {gpsData.lat.toFixed(5)}, {gpsData.lng.toFixed(5)}
                          {gpsData.accuracy && ` · ±${Math.round(gpsData.accuracy)}m accuracy`}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={detectGPS} className="btn-outline" style={{ flex: 1, justifyContent: 'center', padding: '10px' }}>
                      <Crosshair size={14} /> Re-detect
                    </button>
                    <button onClick={handleGpsSelect} className="btn-primary" style={{ flex: 2, justifyContent: 'center', padding: '10px' }}>
                      <Navigation size={14} /> Use This Location
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manual Tab */}
          {tab === 'manual' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input className="form-input" placeholder="Street / Area / Landmark"
                value={manual.street} onChange={e => setManual(m => ({ ...m, street: e.target.value }))} />
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-input" placeholder="City *"
                  value={manual.city} onChange={e => setManual(m => ({ ...m, city: e.target.value }))} style={{ flex: 2 }} />
                <input className="form-input" placeholder="State *"
                  value={manual.state} onChange={e => setManual(m => ({ ...m, state: e.target.value }))} style={{ flex: 2 }} />
                <input className="form-input" placeholder="Pincode"
                  value={manual.pincode} onChange={e => setManual(m => ({ ...m, pincode: e.target.value }))} style={{ flex: 1, minWidth: 80 }} />
              </div>
              <div style={{ background: 'var(--gray-light)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray)', marginBottom: 8 }}>Coordinates (optional — for precise navigation)</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-input" placeholder="Latitude e.g. 17.3850"
                    value={manual.lat} onChange={e => setManual(m => ({ ...m, lat: e.target.value }))} />
                  <input className="form-input" placeholder="Longitude e.g. 78.4867"
                    value={manual.lng} onChange={e => setManual(m => ({ ...m, lng: e.target.value }))} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 6 }}>
                  💡 Find coordinates: <a href="https://maps.google.com" target="_blank" rel="noreferrer" style={{ color: 'var(--orange)' }}>Google Maps</a> → right-click your location → copy numbers
                </div>
              </div>
              <button onClick={handleManualSelect} className="btn-primary" style={{ justifyContent: 'center', padding: '13px', fontSize: 15, marginTop: 4 }}>
                <MapPin size={15} /> Confirm Location
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Navigate to Shop Button ─────────────────────────────────────────────────
export function NavigateToShopButton({ shop, userLocation, style = {} }) {
  const [showPicker, setShowPicker] = useState(false);
  const [myLoc, setMyLoc] = useState(userLocation);

  const shopLat = shop?.location?.lat;
  const shopLng = shop?.location?.lng;
  const hasShopCoords = shopLat && shopLng;

  const distance = myLoc && hasShopCoords
    ? calcDistance(myLoc.lat, myLoc.lng, shopLat, shopLng)
    : null;

  const navigate = (loc) => {
    const from = loc || myLoc;
    if (from && hasShopCoords) {
      openDirections(from.lat, from.lng, shopLat, shopLng, shop.shopName);
    } else if (hasShopCoords) {
      openShopOnMap(shopLat, shopLng, shop.shopName);
    } else {
      // Fallback: search by address
      const q = encodeURIComponent(`${shop.shopName} ${shop.location?.address} ${shop.location?.city}`);
      window.open(`https://www.google.com/maps/search/${q}`, '_blank');
    }
  };

  const handleNavigate = () => {
    if (myLoc) {
      navigate(myLoc);
    } else {
      setShowPicker(true);
    }
  };

  const handleLocationSelected = (loc) => {
    setMyLoc(loc);
    setShowPicker(false);
    navigate(loc);
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
        <button onClick={handleNavigate}
          style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'linear-gradient(135deg,#1A73E8,#0D62C9)', color: 'white', border: 'none', borderRadius: 10, padding: '10px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'var(--font-body)' }}>
          <Navigation size={15} />
          {myLoc ? 'Get Directions' : 'Navigate to Shop'}
        </button>
        {distance !== null && (
          <div style={{ fontSize: 12, color: 'var(--gray)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <MapPin size={11} /> ~{distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`} from your location
          </div>
        )}
        {hasShopCoords && (
          <button onClick={() => openShopOnMap(shopLat, shopLng, shop.shopName)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1.5px solid var(--gray-border)', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: 'var(--dark)', fontFamily: 'var(--font-body)' }}>
            <MapPin size={12} color="var(--orange)" /> View on Map
          </button>
        )}
      </div>

      {showPicker && (
        <LocationPickerModal
          title="Your Location (for directions)"
          onClose={() => setShowPicker(false)}
          onSelect={handleLocationSelected}
        />
      )}
    </>
  );
}

// ─── Static Map Preview (uses OpenStreetMap tiles, no API key) ───────────────
export function StaticMapPreview({ lat, lng, label, height = 180 }) {
  if (!lat || !lng) return null;
  const zoom = 15;
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.01},${lat-0.01},${lng+0.01},${lat+0.01}&layer=mapnik&marker=${lat},${lng}`;
  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--gray-border)', position: 'relative' }}>
      <iframe
        title={label || 'Shop Location'}
        src={mapUrl}
        width="100%"
        height={height}
        style={{ border: 'none', display: 'block' }}
        loading="lazy"
      />
      <div style={{ position: 'absolute', top: 8, right: 8, background: 'white', borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 600, color: 'var(--dark)', boxShadow: 'var(--shadow)', display: 'flex', alignItems: 'center', gap: 4 }}>
        <MapPin size={11} color="var(--orange)" /> {label || 'Shop Location'}
      </div>
    </div>
  );
}

// ─── User Location Bar (shows in pages) ─────────────────────────────────────
export function UserLocationBar({ location, onChangeLocation }) {
  return (
    <div className="user-location-bar" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '9px 14px', cursor: 'pointer' }} onClick={onChangeLocation}>
      <MapPin className="user-location-icon" size={15} color="#3B82F6" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="user-location-label" style={{ fontSize: 11, color: '#3B82F6', fontWeight: 700 }}>YOUR LOCATION</div>
        <div className="user-location-value" style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {location ? (location.display || location.city || `${location.lat?.toFixed(4)}, ${location.lng?.toFixed(4)}`) : 'Tap to set your location'}
        </div>
      </div>
      <Crosshair className="user-location-icon" size={15} color="#3B82F6" style={{ flexShrink: 0 }} />
    </div>
  );
}
